// edit.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { useModal } from '../../../../components/layout';
import { useProfileManagement } from '../../../../components/auth/useProfileManagement';
import ApiSourceForm from '../../../../components/data-sources/api-sources/ApiSourceForm';
import { API_ENDPOINTS } from '../../../../config/api.config';
import { apiService } from '../../../../services/apiService';
import { ApiSourceFormData, ApiSource } from '../../../../types';
import { transformApiSourceToFormData, transformFormDataToApiPayload } from '../../../../utils/apiSourceUtils';
import DeleteConfirmationModal from '../../../../components/modals/common/DeleteConfirmationModal';
import ContentSkeleton from '../../../../components/loading/ContentSkeleton';

interface ApiSourceResponse {
    apiSource: ApiSource;
}

const EditAPISource: React.FC = () => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const modalState = useModal();
    const [formData, setFormData] = useState<ApiSourceFormData | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useProfileManagement(
        undefined,
        modalState.setError,
        modalState.setSuccess,
        modalState.setIsLoading,
        modalState.setShowProfileModal,
        modalState.setShowPasswordModal
    );

    const fetchAPISource = async () => {
        try {
            const apiUrl = API_ENDPOINTS.DATA_SOURCE.API_SOURCES.GET.replace(':id', id || '');
            const response = await apiService.get<ApiSourceResponse>(apiUrl);

            console.log('Fetched API source:', response);
            const formattedData = transformApiSourceToFormData(response.apiSource);
            console.log('Transformed form data:', formattedData);

            setFormData(formattedData);
        } catch (error) {
            console.error('Error fetching API source:', error);
            modalState.setError(error instanceof Error ? error.message : 'Failed to fetch API source');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchAPISource();
        }
    }, [id]);

    const handleSubmit = async (submitData: ApiSourceFormData) => {
        modalState.setIsLoading(true);
        modalState.setError(null);

        try {
            const transformedData = transformFormDataToApiPayload(submitData);
            const apiPayload = {
                ...transformedData,
                name: submitData.name,
                description: submitData.description || '',
                source_type: 'api_query'
            };

            console.log('Submitting API payload:', apiPayload);

            const updateUrl = API_ENDPOINTS.DATA_SOURCE.API_SOURCES.UPDATE.replace(':id', id || '');
            await apiService.put(updateUrl, apiPayload);

            modalState.setSuccess('API source updated successfully');
            navigate('/data-sources/api');
        } catch (error) {
            console.error('Error updating API source:', error);
            modalState.setError(error instanceof Error ? error.message : 'An error occurred');
        } finally {
            modalState.setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        modalState.setIsLoading(true);
        modalState.setError(null);

        try {
            const deleteUrl = API_ENDPOINTS.DATA_SOURCE.API_SOURCES.DELETE.replace(':id', id || '');
            await apiService.delete(deleteUrl);

            modalState.setSuccess('API source deleted successfully');
            navigate('/data-sources/api');
        } catch (error) {
            modalState.setError(error instanceof Error ? error.message : 'An error occurred');
        } finally {
            modalState.setIsLoading(false);
            setShowDeleteModal(false);
        }
    };

    const handleCancel = () => {
        navigate('/data-sources/api');
    };

    if (!user) return null;

    return (
        <div className="page-container">
            <div className="content-header">
                <h5 className="mb-4 fw-semibold">Edit API Source</h5>
                {isLoading ? (
                    <ContentSkeleton />
                ) : (
                    <>
                        <ApiSourceForm
                            initialData={formData || undefined}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            onDelete={async () => setShowDeleteModal(true)}
                            error={modalState.error}
                            isLoading={modalState.isLoading}
                            isEdit={true}
                        />
                        <DeleteConfirmationModal
                            isOpen={showDeleteModal}
                            onClose={() => setShowDeleteModal(false)}
                            onConfirm={handleDelete}
                            resourceType="API Source"
                            resourceName={formData?.name || ''}
                            isLoading={modalState.isLoading}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default EditAPISource;